import React, { Component, lazy, memo, Suspense } from 'react';
import { Button } from '@Sage/hoi-poi-ui';
import { isChunkLoadError } from '@Sage/ssm-utils';

import AbsoluteEmpty from 'components/AbsoluteEmpty';
import LoaderHoi from 'components/LoaderHoi';
import {
  attemptStaleChunkSelfHeal,
  reportExhaustedChunkFailure,
} from 'utils/chunkLoadRecovery';
import { getLiteral } from 'utils/getLiteral';

const RichText = lazy(() => import('./RichTextAdapter'));

const reloadPage = () => window.location.reload();

class RichTextAdapterChunkErrorBoundary extends Component {
  state = { error: null, selfHealing: false };

  static getDerivedStateFromError(error) {
    return { error, selfHealing: false };
  }

  componentDidCatch(error) {
    if (!isChunkLoadError(error)) return;

    if (attemptStaleChunkSelfHeal(error)) {
      this.setState({ selfHealing: true });
      return;
    }

    reportExhaustedChunkFailure(error);
  }

  render() {
    const { children, onReload } = this.props;
    const { error, selfHealing } = this.state;

    if (!error) return children;
    if (!isChunkLoadError(error)) throw error;
    if (selfHealing) return <LoaderHoi size="big" />;

    return (
      <AbsoluteEmpty
        actions={[
          <Button key="reload-rich-text" onClick={onReload}>
            {getLiteral('action_refresh')}
          </Button>,
        ]}
      />
    );
  }
}

const LazyRichText = memo((props) => {
  return (
    <RichTextAdapterChunkErrorBoundary onReload={reloadPage}>
      <Suspense fallback={<LoaderHoi size="big" />}>
        <RichText {...props} />
      </Suspense>
    </RichTextAdapterChunkErrorBoundary>
  );
});

LazyRichText.displayName = 'LazyRichText';

export default LazyRichText;
